import { useEffect, useState, Fragment, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
    Typography, Grid, Select, MenuItem, Button, FormControl, Tooltip, Link,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination,
    Box, Snackbar, Alert
} from '@mui/material';
import { Breadcrumb } from 'components';
import { getV2Quote, submitV2Quote } from 'services/projects';

export const V2Quote = () => {
    const [prices, setPrices] = useState<Record<string, string>>({});
    const navigate = useNavigate();
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        data: [],
        rowsPerPage: 10,
        total: 0
    });
    const [error, setError] = useState({
        open: false,
        type: '',
        message: ''
    });
    const queryParams = new URLSearchParams(window.location.search);
    const quoteId = queryParams.get('id');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        loadV2QuoteData();
    }, []);

    const loadV2QuoteData = async () => {
        try {
            const response = await getV2Quote(quoteId);
            if (response.status === 200) {
                setPaginationModel((prev) => ({
                    ...prev,
                    data: response.data,
                    total: response.data.length
                }));
                // Update state with the fetched data
            } else {
                setPaginationModel((prev) => ({
                    ...prev,
                    data: [],
                    total: 0
                }));
            }
        } catch (error) {
            setError({ open: true, type: 'error', message: 'Error fetching V2 Quote data' });
            setPaginationModel((prev) => ({
                ...prev,
                data: [],
                total: 0
            }));
        }
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPaginationModel((prev) => ({
            ...prev,
            page: newPage
        }));
    }

    const handlePrice = (id: string, value: string) => {
        setPrices(prev => ({ ...prev, [id]: value }));
    };

    const handleSubmit = async () => {
        const payload = paginationModel.data.map((item: any) => ({
            ...item,
            testPrice: item?.discountedPrices?.length > 1
                ? (prices[item.id] ?? 0)
                : (item?.discountedPrices?.[0] ?? 0),
        }));
        try {
            const response = await submitV2Quote(payload);
            if (response.status === 200) {
                setError({ open: true, type: 'success', message: 'V2 Quote submitted successfully' });
                setTimeout(() => navigate('/home/viewQuote'), 2000);
            } else {
                setError({ open: true, type: 'error', message: 'Error submitting V2 Quote' });
            }
        } catch (error) {
            setError({ open: true, type: 'error', message: 'Error submitting V2 Quote' });
        }
    };

    const allDropdownsFilled = (paginationModel.data as any[])
        .filter((item: any) => item?.discountedPrices?.length > 1)
        .every((item: any) => !!prices[item.id]);

    const handleClick = (user: any) => {
        const updatedData = paginationModel.data.map((item: any) => {
            if (item.id === user.id) {
                return {
                    ...item,
                    isOpen: !item.isOpen,
                    linkedTests: item.linkedTests,
                };
            }

            return item;
        });

        setPaginationModel((prev: any) => ({
            ...prev,
            data: updatedData,
        }));
    };

    const childRender = (item: any) => {
        return (
            <>
                {item.linkedTests.map((child: any, index: any) => {
                    return (
                        <Fragment key={child.id}>
                            <TableRow tabIndex={-1} key={child.index} style={{ backgroundColor: '#00388324' }}>
                                <TableCell style={{ padding: '12px 4px' }}>{child.code}</TableCell >
                                <TableCell style={{ padding: '12px 4px' }}>{child.testName}</TableCell>
                                <TableCell style={{ padding: '12px 4px' }}>{child.matrixType}</TableCell>
                                <TableCell style={{ padding: '12px 4px' }}>{child.testPrice}</TableCell>
                            </TableRow>
                        </Fragment>
                    );
                })}
            </>
        )
    }

    let nav: any = [{ name: 'Home', path: '/home/viewQuote' }, { name: 'V2 Quote', path: '/home/v2-quote' }];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', cursor: 'pointer' }}>
                <Breadcrumb navList={nav} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                <Typography variant="h6" gutterBottom align='left' mt={1}>
                    V2 Quote
                </Typography>
            </div>
            <Grid container spacing={2}>
                <Grid item xs={12} md={12} sm={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!allDropdownsFilled}>
                            Submit
                        </Button>
                    </Box>
                    <TableContainer>
                        <Table sx={{ minWidth: 700, border: '1px solid #ddd' }} aria-label="custom table" id="customTable">
                            <TableHead style={{ backgroundColor: 'rgb(0, 56, 131)' }}>
                                <TableRow style={{ height: '50px' }}>
                                    <TableCell style={{ color: '#fff', padding: '4px', width: '100px' }}>Code</TableCell>
                                    <TableCell style={{ color: '#fff', padding: '4px', width: '100px' }}>Name</TableCell>
                                    <TableCell style={{ color: '#fff', padding: '4px', width: '100px' }}>Matrix Type</TableCell>
                                    <TableCell style={{ color: '#fff', padding: '4px', width: '60px' }}>Price Per Sample</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginationModel.data && paginationModel.data.length > 0 ? paginationModel.data.map((user: any, index: any) => {
                                    return (
                                        <Fragment key={user.id}>
                                            <TableRow tabIndex={-1} key={user.id}>
                                                <TableCell style={{ padding: '12px 4px' }}>
                                                    {user.code}
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        padding: '12px 4px',
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                        overflowWrap: 'anywhere',
                                                    }}
                                                >
                                                    <Tooltip title={user.testName || ''} arrow>
                                                        <span>
                                                            {user?.linkedTests?.length > 0 ? (
                                                                <Link
                                                                    component="button"
                                                                    variant="body2"
                                                                    key={user.id}
                                                                    onClick={() => handleClick(user)}
                                                                >
                                                                    {user.testName}
                                                                </Link>
                                                            ) : (
                                                                <>{user.testName}</>
                                                            )}
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell style={{ padding: '12px 4px' }}>
                                                    {user.matrixType}
                                                </TableCell>
                                                <TableCell style={{ padding: '12px 4px' }}>
                                                    {user?.discountedPrices?.length > 1 ? (
                                                        <FormControl
                                                            size="small"
                                                            sx={{
                                                                width: "120px", // Fixed width
                                                                "& .MuiOutlinedInput-root": {
                                                                    height: 30,
                                                                    fontSize: "12px",
                                                                },
                                                            }}
                                                        >
                                                            <Select
                                                                value={prices[user.id] || ""}
                                                                onChange={(event) => handlePrice(user.id, event.target.value)}
                                                                displayEmpty
                                                                sx={{
                                                                    fontSize: "12px",
                                                                    "& .MuiSelect-select": {
                                                                        py: "4px",
                                                                        px: "8px",
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                    },
                                                                }}
                                                                MenuProps={{
                                                                    PaperProps: {
                                                                        sx: {
                                                                            maxHeight: 180,
                                                                            "& .MuiMenuItem-root": {
                                                                                fontSize: "12px",
                                                                                minHeight: 30,
                                                                            },
                                                                        },
                                                                    },
                                                                }}
                                                                renderValue={(selected) => {
                                                                    if (!selected) {
                                                                        return <span style={{ color: "#999" }}>Select Value</span>;
                                                                    }
                                                                    return selected;
                                                                }}
                                                            >
                                                                <MenuItem value="" disabled>
                                                                    Select Value
                                                                </MenuItem>

                                                                {user?.discountedPrices?.map((item: any) => (
                                                                    <MenuItem key={item} value={item}>
                                                                        {item}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        <Typography variant="body2">
                                                            {user?.discountedPrices?.[0] || "0"}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            {user.isOpen ? childRender(user) : null}
                                        </Fragment>
                                    )
                                }) : (
                                    <TableRow>
                                        <TableCell
                                            // colSpan={labUserCheck?.UserType === 'Internal' ? 7 : 7}
                                            align="center"
                                            style={{ padding: '24px', color: 'rgba(0, 0, 0, 0.6)' }}
                                        >
                                            No rows
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[10]}
                            component="div"
                            count={paginationModel?.total}
                            page={paginationModel?.page}
                            onPageChange={handleChangePage}
                            rowsPerPage={paginationModel?.rowsPerPage}
                        />
                    </TableContainer>
                </Grid>
            </Grid>

            <Snackbar
                open={error?.open}
                autoHideDuration={6000}
                onClose={() => {
                    setError({ open: false, type: '', message: '' });
                }}
            >
                <Alert
                    onClose={() => setError({ open: false, type: '', message: '' })}
                    severity={error?.type === 'success' ? 'success' : 'error'}
                    sx={{ width: '100%' }}
                >
                    <b>{error?.message}</b>
                </Alert>
            </Snackbar>
        </div>
    );
}
